/**
 * Web-vitals persistence.
 *
 * Streams field-measured Core Web Vitals (LCP, CLS, INP, FCP, TTFB) plus the
 * font-swap timing (from `document.fonts.ready`) and the deferred third-party
 * cost into `public.web_vitals`, tagged with the app `release` string that
 * Vite bakes in at build time. That lets us diff loading metrics
 * release-over-release — the source of truth for whether a font/image change
 * actually helped.
 *
 * Everything here is best-effort: no throw ever escapes, and the network
 * insert uses `keepalive` so a `pagehide` firing mid-flight still gets logged.
 */
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from "web-vitals";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";

// Release string is injected by vite `define` — see vite.config.ts.
declare const __APP_RELEASE__: string;
const RELEASE = typeof __APP_RELEASE__ === "string" ? __APP_RELEASE__ : "dev";

interface DeviceContext {
  connection_type: string | null;
  device_memory: number | null;
  hardware_concurrency: number | null;
  save_data: boolean | null;
}

function deviceContext(): DeviceContext {
  const nav = navigator as Navigator & {
    connection?: { effectiveType?: string; saveData?: boolean };
    deviceMemory?: number;
  };
  return {
    connection_type: nav.connection?.effectiveType ?? null,
    device_memory: typeof nav.deviceMemory === "number" ? nav.deviceMemory : null,
    hardware_concurrency: typeof nav.hardwareConcurrency === "number" ? nav.hardwareConcurrency : null,
    save_data: nav.connection?.saveData ?? null,
  };
}

// Persist a session id across the page's lifetime so multiple metrics from the
// same visit can be joined at analysis time.
//
// Resolved LAZILY, on first report. Generating a random value while this module
// is being loaded is a forbidden operation in the edge/worker runtime that
// serves SSR, and since every route imports this module through the router
// chunk, doing it at module scope 500s the entire site.
let _sessionId: string | undefined;

function sessionId(): string {
  if (_sessionId) return _sessionId;
  try {
    const existing = sessionStorage.getItem("statlign_vitals_session");
    if (existing) {
      _sessionId = existing;
      return _sessionId;
    }
    const fresh = crypto.randomUUID();
    sessionStorage.setItem("statlign_vitals_session", fresh);
    _sessionId = fresh;
  } catch {
    _sessionId = crypto.randomUUID();
  }
  return _sessionId;
}

async function persist(row: {
  metric: string;
  value: number;
  rating?: string | null;
  navigation_type?: string | null;
}) {
  const payload = {
    release: RELEASE,
    route: window.location.pathname,
    metric: row.metric,
    value: row.value,
    rating: row.rating ?? null,
    navigation_type: row.navigation_type ?? null,
    session_id: sessionId(),
    user_agent: navigator.userAgent.slice(0, 500),
    ...deviceContext(),
  };

  // Mirror to analytics so it's queryable in GA4 immediately.
  try {
    trackEvent("web_vital", {
      release: RELEASE,
      route: payload.route,
      metric: row.metric,
      value: Math.round(row.value),
      rating: row.rating ?? "unknown",
    });
  } catch {
    /* best-effort */
  }

  try {
    // Crawlers and headless browsers skew real-visitor speed figures.
    if (navigator.webdriver || /bot|crawl|spider|slurp|headless|inspectiontool|lighthouse/i.test(navigator.userAgent)) return;
    await supabase.from("web_vitals").insert(payload);
  } catch {
    /* offline / RLS reject — don't disturb the app */
  }
}

function reportMetric(m: Metric) {
  void persist({
    metric: m.name,
    value: m.value,
    rating: m.rating,
    navigation_type: m.navigationType,
  });
}

let started = false;

/**
 * Start collecting Core Web Vitals + font-swap timing. Safe to call multiple
 * times; the underlying `web-vitals` handlers are registered only once. Skips
 * initialisation on non-published hosts (preview/localhost) so synthetic runs
 * don't pollute release baselines.
 */
export function initWebVitalsTracking(): void {
  if (started || typeof window === "undefined") return;
  started = true;

  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1" || host.includes("id-preview")) {
    // Still emit to the console in dev so we can eyeball metrics locally.
    if (import.meta.env.DEV) {
      onLCP((m) => console.info("[web-vitals]", m.name, m.value));
      onCLS((m) => console.info("[web-vitals]", m.name, m.value));
      onINP((m) => console.info("[web-vitals]", m.name, m.value));
    }
    return;
  }

  onLCP(reportMetric);
  onCLS(reportMetric);
  onINP(reportMetric);
  onFCP(reportMetric);
  onTTFB(reportMetric);

  // Font-swap timing — the wall-clock delay between navigation and the moment
  // every declared font finishes swapping in. Directly measures the impact of
  // moving Inter to a self-hosted variable subset with `font-display: swap`.
  try {
    if (document.fonts && typeof document.fonts.ready?.then === "function") {
      document.fonts.ready.then(() => {
        const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
        const start = nav?.startTime ?? 0;
        const value = performance.now() - start;
        if (Number.isFinite(value) && value > 0) {
          void persist({ metric: "FONT_SWAP", value });
        }
      });
    }
  } catch {
    /* Font Loading API not available */
  }

  // Third-party cost, mirrored from the `third-party-tags` performance mark
  // set up in index.html. Recorded here (not in thirdPartyPerf.ts) so it lands
  // in the same release-tagged table as the vitals.
  window.addEventListener("pagehide", () => {
    try {
      const tag = performance.getEntriesByName("third-party-tags")[0];
      if (tag && tag.duration > 0) {
        void persist({ metric: "TP_LOAD", value: tag.duration });
      }
    } catch {
      /* ignore */
    }
  }, { once: true });
}
